import { useMutation, useQuery } from '@tanstack/react-query';

import { HStack } from '@/components/ui/hstack';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { getCurrentUserV2QueryOptions, type CurrentUserV2 } from '@/services/getCurrentUserV2';
import { setUserStatusV2, type SetUserStatusV2Request } from '@/services/setUserStatusV2';
import { queryClient } from '@/util/queryClient';

/**
 * The original Active/Away switch, kept whole for when the expert away calendar
 * flag is off. It writes the account status directly, with an optimistic update
 * that rolls back on error, and touches nothing about away schedules. It reads the
 * user through the same query the Account screen does, so it adds no request.
 */
export function LegacyAwaySwitch({ userId }: { userId?: string }) {
  const { colors } = useColors();

  const userQueryKey = getCurrentUserV2QueryOptions(userId).queryKey;

  const { data } = useQuery(getCurrentUserV2QueryOptions(userId));

  const statusIsAway = data?.status === '2';

  const { mutate } = useMutation({
    mutationFn: setUserStatusV2,
    onMutate: async ({ status }: SetUserStatusV2Request) => {
      await queryClient.cancelQueries({ queryKey: userQueryKey });

      const previousUserData = await queryClient.getQueryData(userQueryKey);

      queryClient.setQueryData(userQueryKey, (old?: CurrentUserV2) => {
        if (old) {
          return {
            ...old,
            status,
          };
        }

        return old;
      });

      return { previousUserData };
    },
    onError: (error, newUserStatus, context) => {
      console.error(`Account setUserStatus:${JSON.stringify(newUserStatus)} ERROR:`, error);

      queryClient.setQueryData(userQueryKey, context?.previousUserData);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });

  return (
    <HStack className="mb-4 w-full items-start justify-between gap-4">
      <VStack className="flex-shrink">
        <Text>Set as away</Text>

        <Text size="xs" className="text-typography-700">
          Setting your status to away will hide you from future search results. You may still
          communicate in open conversations and start inquiries.
        </Text>
      </VStack>

      <Switch
        trackColor={{ true: colors.secondary[500], false: colors.background[200] }}
        value={statusIsAway}
        onValueChange={(value: boolean) => {
          mutate({ status: value ? '2' : '1' });
        }}
      />
    </HStack>
  );
}
